import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import { cmsService } from "@client/http/cms.service";
import { IncidentList } from "../BulkIngest/IncidentList";
import {
  getMediaCollectionsSelector,
  GetCollectionsAction,
} from "../../../store/reducers/cmsReducer";
import type { CMSCollection } from "@client/models/CMS/cmsCollection";
import type {
  IIngestJob,
  IIngestPhaseEntry,
  IngestJobPhase,
  IngestTranscoder,
} from "@nendaTypes/NendaTypes";
import {
  FailedIngestPhase,
  isActiveIngestPhase,
  resolveFailedPhase,
} from "@shared/ingestPhases";
import {
  resolveIngestTranscoder,
  selectTranscoderReport,
} from "@shared/ingestTranscoder";

// UI-only states that extend the DB phases
type LocalPhase = "idle" | "uploading" | IngestJobPhase | "error";

interface Props {
  /** The CMS asset ID this panel is scoped to */
  assetId: string;
  /** Whether the asset already has media */
  hasMedia: boolean;
  /** Callback to refresh the asset after ingest completes */
  onIngestComplete?: () => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5_000;

const STEPS = [
  "Upload",
  "Queued",
  "Transcoding",
  "Assembly",
  "Ingesting",
  "Publishing",
];

/** Which step of STEPS stands for the phase a failed job failed in. */
const STEP_BY_FAILED_PHASE: Record<FailedIngestPhase, number> = {
  queued: 1,
  transcoding: 2,
  assembly: 3,
  ingesting: 4,
  publishing: 5,
};

const SUBTITLE_LANGUAGES = [
  { code: "sv", label: "Swedish" },
  { code: "en", label: "English" },
  { code: "no", label: "Norwegian" },
  { code: "da", label: "Danish" },
  { code: "fi", label: "Finnish" },
];

type SubtitleRow = { id: number; language: string; file: File | null };

/** How each transcoder is named in the UI. */
const TRANSCODER_LABEL: Record<IngestTranscoder, string> = {
  encore: "Encore",
  videocore: "Videocore",
};

// ── Helper functions ──────────────────────────────────────────────────────────

function stepToPhase(step: number): IngestJobPhase | "upload" | null {
  switch (step) {
    case 0:
      return "upload";
    case 1:
      return "queued";
    case 2:
      return "transcoding";
    case 3:
      return "assembly";
    case 4:
      return "ingesting";
    case 5:
      return "publishing";
    default:
      return null;
  }
}

function getPhaseData(
  phaseName: IngestJobPhase | "upload",
  job: IIngestJob | null
): IIngestPhaseEntry | null {
  if (!job) return null;

  // Special case for upload - return a synthetic entry
  if (phaseName === "upload") {
    return {
      phase: "queued", // Dummy phase
      startedAt: job.createdAt || new Date(),
      completedAt: job.phaseLog[0]?.startedAt || job.currentPhase.startedAt,
    } as IIngestPhaseEntry;
  }

  // For completed phases: find in phaseLog
  const logEntry = job.phaseLog.find((p) => p.phase === phaseName);
  if (logEntry) return logEntry;

  // For current phase: use currentPhase
  if (job.currentPhase.phase === phaseName) return job.currentPhase;

  // Special case: if job failed, check if this is the phase where it failed
  if (
    job.currentPhase.phase === "failed" &&
    resolveFailedPhase(job) === phaseName
  ) {
    return job.currentPhase;
  }

  return null; // Phase not reached yet
}

function formatDuration(startedAt?: Date, completedAt?: Date): string {
  if (!startedAt) return "N/A";
  if (!completedAt) return "In progress";

  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${seconds}s`;
}

function formatDateTime(date?: Date | string): string {
  if (!date) return "N/A";
  const d = new Date(date);
  return d.toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "medium",
  });
}

function phaseToStep(phase: LocalPhase, job?: IIngestJob | null): number {
  switch (phase) {
    case "idle":
    case "uploading":
    case "error":
      return 0;
    case "queued":
      return 1;
    case "transcoding":
      return 2;
    case "assembly":
      return 3;
    case "ingesting":
      return 4;
    case "publishing":
      return 5;
    case "completed":
      return 5; // Show as Publishing complete
    case "failed": {
      if (!job) return 0;
      return STEP_BY_FAILED_PHASE[resolveFailedPhase(job)];
    }
    default:
      return 0;
  }
}

function isInFlight(phase: LocalPhase): boolean {
  return phase === "uploading" || isActiveIngestPhase(phase);
}

// ── Component ─────────────────────────────────────────────────────────────────

export const IngestPanel: React.FC<Props> = ({
  assetId,
  hasMedia,
  onIngestComplete,
}) => {
  const dispatch = useDispatch();
  const collections = useSelector(
    getMediaCollectionsSelector
  ) as CMSCollection[];
  const [selectedEntitlements, setSelectedEntitlements] = useState<string[]>(
    []
  );
  const [phase, setPhase] = useState<LocalPhase>("idle");
  const [job, setJob] = useState<IIngestJob | null>(null);
  const [userSelectedStep, setUserSelectedStep] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const subtitleNextId = useRef(1);
  const [subtitleRows, setSubtitleRows] = useState<SubtitleRow[]>([
    { id: 0, language: "sv", file: null },
  ]);
  const [subtitleUploading, setSubtitleUploading] = useState(false);
  const [subtitleMessage, setSubtitleMessage] = useState<string | null>(null);
  const [subtitleError, setSubtitleError] = useState<string | null>(null);
  const [jobDetailsExpanded, setJobDetailsExpanded] = useState(true);

  // ── Restore active job on mount ──────────────────────────────────────────

  useEffect(() => {
    if (collections.length === 0) {
      dispatch(GetCollectionsAction());
    }
  }, [dispatch, collections.length]);

  useEffect(() => {
    let cancelled = false;
    cmsService
      .getActiveIngestJob(assetId)
      .toPromise()
      .then((res) => {
        if (cancelled) return;
        const data = res as { job: IIngestJob | null };
        if (data?.job) {
          setJob(data.job);
          setPhase(data.job.currentPhase.phase);
        }
      })
      .catch((err) => {
        console.error("Failed to restore active job:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  // Collapse accordion when job completes
  useEffect(() => {
    if (job?.currentPhase.phase === "completed") {
      setJobDetailsExpanded(false);
    }
  }, [job?.currentPhase.phase]);

  // ── Polling ──────────────────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(
    (jobId: string) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const res = await cmsService.getVodStatus(jobId).toPromise();
          const updated = res as IIngestJob;
          setJob(updated);
          setPhase(updated.currentPhase.phase);
          if (
            updated.currentPhase.error ||
            updated.currentPhase.phase === "completed"
          ) {
            stopPolling();
            // Refresh asset when ingest completes successfully
            if (
              updated.currentPhase.phase === "completed" &&
              onIngestComplete
            ) {
              onIngestComplete();
            }
          }
        } catch (err) {
          console.error("Poll status error:", err);
        }
      }, POLL_INTERVAL_MS);
    },
    [stopPolling, onIngestComplete]
  );

  // Start/stop polling based on phase
  useEffect(() => {
    if (!job) return;

    const shouldPoll = isActiveIngestPhase(phase);

    if (shouldPoll) {
      pollStatus(String(job._id));
    } else {
      stopPolling();
    }

    return stopPolling;
  }, [phase, job, pollStatus, stopPolling]);

  // ── Subtitle helpers ─────────────────────────────────────────────────────

  const addSubtitleRow = () => {
    setSubtitleRows((prev) => [
      ...prev,
      { id: subtitleNextId.current++, language: "sv", file: null },
    ]);
  };

  const updateSubtitleRow = (id: number, patch: Partial<SubtitleRow>) => {
    setSubtitleRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );
  };

  const removeSubtitleRow = (id: number) => {
    setSubtitleRows((prev) =>
      prev.length > 1 ? prev.filter((r) => r.id !== id) : prev
    );
  };

  const handleSubtitleUpload = async () => {
    const toUpload = subtitleRows.filter(
      (r): r is SubtitleRow & { file: File } => r.file !== null
    );
    if (toUpload.length === 0) return;
    setSubtitleUploading(true);
    setSubtitleMessage(null);
    setSubtitleError(null);
    let ok = 0;
    let fail = 0;
    for (const row of toUpload) {
      try {
        await cmsService
          .uploadSubtitles(assetId, row.file, row.language)
          .toPromise();
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setSubtitleUploading(false);
    if (ok > 0) {
      setSubtitleMessage(
        `Uploaded ${ok} subtitle file(s).${fail > 0 ? ` ${fail} failed.` : ""}`
      );
      setSubtitleRows((prev) => prev.map((r) => ({ ...r, file: null })));
    }
    if (fail > 0 && ok === 0) {
      setSubtitleError(`All ${fail} subtitle upload(s) failed.`);
    }
  };

  // ── Retry job ────────────────────────────────────────────────────────────

  const handleRetry = useCallback(async () => {
    if (!job) return;
    try {
      setError(null);
      await cmsService.retryIngestJob(String(job._id)).toPromise();
      // Refresh job status
      const res = await cmsService.getVodStatus(String(job._id)).toPromise();
      const updated = res as IIngestJob;
      setJob(updated);
      setPhase(updated.currentPhase.phase);
      // Start polling if job is now active
      if (
        updated.currentPhase.phase !== "completed" &&
        updated.currentPhase.phase !== "failed"
      ) {
        pollStatus(String(updated._id));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to retry job");
    }
  }, [job, pollStatus]);

  // ── Upload + trigger flow ────────────────────────────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setError(null);
  };

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setPhase("uploading");
    setUploadProgress(0);
    setError(null);

    try {
      // 1. Request a presigned upload URL
      const urlRes = await cmsService
        .requestStorageUploadUrl({
          fileName: `${assetId}.mp4`,
          contentType: selectedFile.type,
          objectPrefix: "",
          expiresInSeconds: 600,
        })
        .toPromise();

      const { uploadUrl, publicUrl } = urlRes as {
        uploadUrl: string;
        publicUrl: string;
      };

      // 2. Upload blocks via service (handles block-by-block protocol + commit)
      await cmsService.uploadFileToPresignedUrl({
        uploadUrl,
        file: selectedFile,
        onProgress: setUploadProgress,
      });

      // 3. Trigger the ingest job
      const triggerRes = await cmsService
        .triggerVodIngest({
          sourceUrl: publicUrl,
          assetId,
          entitlements: selectedEntitlements.length
            ? selectedEntitlements
            : undefined,
        })
        .toPromise();

      const { jobId } = triggerRes as { jobId: string };

      setJob({
        _id: jobId,
        assetId,
        currentPhase: {
          phase: "queued",
          startedAt: new Date(),
        },
        phaseLog: [],
        createdBy: "",
        sourceUrl: publicUrl,
      } as IIngestJob);
      setPhase("queued");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred"
      );
      setPhase("error");
    }
  }, [selectedFile, assetId, selectedEntitlements]);

  // ── Render ───────────────────────────────────────────────────────────────

  // Don't show panel if asset has media but no ingest job exists
  if (hasMedia && !job) {
    return null;
  }

  let activeStep = phaseToStep(phase, job);
  // When job is completed, set activeStep beyond last step so Publishing shows as complete
  if (phase === "completed") {
    activeStep = STEPS.length; // 5 - beyond last step index
  }
  const busy = isInFlight(phase);

  // Derive which step to show details for: user selection takes precedence, otherwise show active step
  let viewedStep = userSelectedStep ?? (phase !== "idle" ? activeStep : null);
  // Cap viewedStep at the last actual step index (for completed jobs)
  if (viewedStep !== null && viewedStep >= STEPS.length) {
    viewedStep = STEPS.length - 1; // Show last step (Publishing) details
  }
  const viewedPhaseName = viewedStep !== null ? stepToPhase(viewedStep) : null;
  const viewedPhaseData = viewedPhaseName
    ? getPhaseData(viewedPhaseName, job)
    : null;
  const transcoder = resolveIngestTranscoder(job);
  const isVideocore = transcoder === "videocore";
  const phaseDetails = viewedPhaseData?.details;
  const {
    status: transcoderStatus,
    message: transcoderMessage,
    jobResponse: transcoderJobResponse,
  } = selectTranscoderReport(transcoder, phaseDetails);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header with toggle when job exists */}
      {job ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Video Ingest</Typography>
          <Button
            size="small"
            onClick={() => setJobDetailsExpanded(!jobDetailsExpanded)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: jobDetailsExpanded ? "rotate(180deg)" : "none",
                  transition: "0.3s",
                }}
              />
            }
          >
            {jobDetailsExpanded ? "Hide" : "Show"} Details
          </Button>
        </Stack>
      ) : (
        <Typography variant="h6" gutterBottom>
          Video Ingest
        </Typography>
      )}

      {/* All job details - collapsible */}
      {job && jobDetailsExpanded && (
        <>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {STEPS.map((label, idx) => {
              const phaseName = stepToPhase(idx);
              const phaseData = phaseName ? getPhaseData(phaseName, job) : null;
              const hasData = !!phaseData;
              const isClickable = hasData;
              const isViewed = viewedStep === idx;
              const isActive = activeStep === idx;
              const hasError = hasData && !!phaseData?.error;

              return (
                <Step key={label}>
                  <StepLabel
                    error={hasError}
                    onClick={
                      isClickable ? () => setUserSelectedStep(idx) : undefined
                    }
                    sx={(theme) => ({
                      cursor: isClickable ? "pointer" : "default",
                      "& .MuiStepLabel-label": {
                        color: isViewed ? "#0F007A !important" : "#727272",
                        fontWeight: isViewed ? 600 : 400,
                      },
                      "& .MuiStepLabel-label.Mui-active": {
                        color: isViewed ? "#0F007A !important" : "#0F007A",
                      },
                      "& .MuiStepLabel-label.Mui-completed": {
                        color: isViewed ? "#0F007A !important" : "#727272",
                      },
                      // Style the circle icon - use primary colors, except for errors
                      "& .MuiStepIcon-root": {
                        color: hasError
                          ? undefined
                          : `${isViewed || isActive ? theme.palette.primary.light : theme.palette.primary.main} !important`,
                      },
                      "& .MuiStepIcon-root.Mui-active": {
                        color: hasError
                          ? undefined
                          : `${isViewed || isActive ? theme.palette.primary.light : theme.palette.primary.main} !important`,
                      },
                      "& .MuiStepIcon-root.Mui-completed": {
                        color: hasError
                          ? undefined
                          : `${isViewed || isActive ? theme.palette.primary.light : theme.palette.primary.main} !important`,
                      },
                      "& .MuiStepIcon-text": {
                        fill: "#FFFFFF !important",
                      },
                    })}
                  >
                    {label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {/* Job Info Card */}
          <Card sx={{ mb: 3, bgcolor: "grey.50" }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Job Information
              </Typography>
              <Stack direction="row" spacing={4} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.primary">
                    Job ID
                  </Typography>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    fontSize="0.75rem"
                  >
                    {job._id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.primary">
                    Asset ID
                  </Typography>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    fontSize="0.75rem"
                  >
                    {job.assetId}
                  </Typography>
                </Box>
                {job.encoreJobId && (
                  <Box>
                    <Typography variant="caption" color="text.primary">
                      Encore Job ID
                    </Typography>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontSize="0.75rem"
                    >
                      {job.encoreJobId}
                    </Typography>
                  </Box>
                )}
                {job.videocore && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.primary">
                        Videocore Job ID
                      </Typography>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontSize="0.75rem"
                      >
                        {job.videocore.transcodeJobId || "Not requested yet"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.primary">
                        Videocore Stack
                      </Typography>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontSize="0.75rem"
                      >
                        {job.videocore.stack}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.primary">
                        Videocore Profile
                      </Typography>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontSize="0.75rem"
                      >
                        {job.videocore.profile}
                      </Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Phase Details Panel */}
          {viewedPhaseData && viewedPhaseName && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6" gutterBottom>
                    {STEPS[viewedStep!]} Details
                  </Typography>

                  <Divider />

                  <Table size="small">
                    <TableBody>
                      {/* Upload-specific fields */}
                      {viewedPhaseName === "upload" && job && (
                        <>
                          <TableRow>
                            <TableCell>
                              <strong>Source URL</strong>
                            </TableCell>
                            <TableCell sx={{ wordBreak: "break-all" }}>
                              {job.sourceUrl}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>Upload Completed</strong>
                            </TableCell>
                            <TableCell>
                              {formatDateTime(viewedPhaseData?.startedAt)}
                            </TableCell>
                          </TableRow>
                        </>
                      )}

                      {/* Common fields for all phases except upload */}
                      {viewedPhaseName !== "upload" && (
                        <>
                          <TableRow>
                            <TableCell>
                              <strong>Started</strong>
                            </TableCell>
                            <TableCell>
                              {formatDateTime(viewedPhaseData.startedAt)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>Completed</strong>
                            </TableCell>
                            <TableCell>
                              {formatDateTime(viewedPhaseData.completedAt)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>Duration</strong>
                            </TableCell>
                            <TableCell>
                              {formatDuration(
                                viewedPhaseData.startedAt,
                                viewedPhaseData.completedAt
                              )}
                            </TableCell>
                          </TableRow>
                        </>
                      )}

                      {/* Retry/Error fields - only for phases with retry logic (queued, transcoding, ingesting, publishing) */}
                      {viewedPhaseName !== "upload" &&
                        viewedPhaseName !== "completed" && (
                          <>
                            <TableRow>
                              <TableCell>
                                <strong>Retry Count</strong>
                              </TableCell>
                              <TableCell>
                                {viewedPhaseData.retryCount ?? 0}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                <strong>Last Retry</strong>
                              </TableCell>
                              <TableCell>
                                {formatDateTime(viewedPhaseData.lastRetryAt)}
                              </TableCell>
                            </TableRow>
                          </>
                        )}

                      {/* Phase-specific fields */}
                      {viewedPhaseName === "queued" && (
                        <TableRow>
                          <TableCell>
                            <strong>Transcoder Status</strong>
                          </TableCell>
                          <TableCell>{transcoderStatus || "N/A"}</TableCell>
                        </TableRow>
                      )}

                      {viewedPhaseName === "assembly" && (
                        <TableRow>
                          <TableCell>
                            <strong>Package</strong>
                          </TableCell>
                          <TableCell>
                            {viewedPhaseData.details
                              ?.packageAssemblyPortalMessage || "N/A"}
                          </TableCell>
                        </TableRow>
                      )}

                      {viewedPhaseName === "transcoding" && (
                        <>
                          <TableRow>
                            <TableCell>
                              <strong>Progress</strong>
                            </TableCell>
                            <TableCell>
                              {viewedPhaseData.progress ?? 0}%
                              {phaseDetails?.videocoreProgressUnreadable && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.primary"
                                >
                                  We could not read the progress just now, so
                                  this number may be out of date. The video is
                                  still being converted.
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>Transcoder</strong>
                            </TableCell>
                            <TableCell>
                              {TRANSCODER_LABEL[transcoder]}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>Transcoder Status</strong>
                            </TableCell>
                            <TableCell>{transcoderStatus || "N/A"}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>Latest Message</strong>
                            </TableCell>
                            <TableCell>
                              {transcoderMessage || "N/A"}
                              {isVideocore && transcoderMessage && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.primary"
                                >
                                  What the transcoder reported. It is a first
                                  clue, not always the real cause — check Issues
                                  During Processing below before acting on it.
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>

                          {isVideocore && (
                            <>
                              <TableRow>
                                <TableCell>
                                  <strong>Copying Video</strong>
                                </TableCell>
                                <TableCell>
                                  {phaseDetails?.videocoreSourceStatus || "N/A"}
                                  {phaseDetails?.videocoreSourceProgress !==
                                    undefined &&
                                    ` — ${phaseDetails.videocoreSourceProgress}%`}
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="text.primary"
                                  >
                                    The video is being copied to the transcoder.
                                    This happens before the conversion starts,
                                    so it is not part of Progress above.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                              {phaseDetails?.videocoreInterruptionReason && (
                                <TableRow>
                                  <TableCell>
                                    <strong>Interrupted</strong>
                                  </TableCell>
                                  <TableCell>
                                    {phaseDetails.videocoreInterruptionReason}
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      color="text.primary"
                                    >
                                      The transcoder stopped and restarted the
                                      conversion on its own. Nothing to do.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                              {!!phaseDetails?.videocoreTranscodeRequestFailures && (
                                <TableRow>
                                  <TableCell>
                                    <strong>Failed Start Attempts</strong>
                                  </TableCell>
                                  <TableCell>
                                    {
                                      phaseDetails.videocoreTranscodeRequestFailures
                                    }{" "}
                                    in a row — the conversion has not been able
                                    to start yet
                                  </TableCell>
                                </TableRow>
                              )}
                              <TableRow>
                                <TableCell>
                                  <strong>Encoder ID</strong>
                                </TableCell>
                                <TableCell sx={{ wordBreak: "break-all" }}>
                                  <Box
                                    component="span"
                                    sx={{ fontFamily: "monospace" }}
                                  >
                                    {phaseDetails?.videocoreEncoreInstanceId ||
                                      "N/A"}
                                  </Box>
                                  {phaseDetails?.videocoreEncoreInstanceId && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      color="text.primary"
                                    >
                                      The machine doing the conversion. Give
                                      this to support if you report a problem.
                                      It is shut down shortly after the job
                                      finishes.
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            </>
                          )}

                          {!isVideocore && (
                            <>
                              <TableRow>
                                <TableCell>
                                  <strong>Conversion Started</strong>
                                </TableCell>
                                <TableCell>
                                  {formatDateTime(
                                    phaseDetails?.encoreStartedDate
                                  )}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>
                                  <strong>Conversion Completed</strong>
                                </TableCell>
                                <TableCell>
                                  {formatDateTime(
                                    phaseDetails?.encoreCompletedDate
                                  )}
                                </TableCell>
                              </TableRow>
                              {phaseDetails?.transcodeCancelledAt && (
                                <TableRow>
                                  <TableCell>
                                    <strong>Cancelled</strong>
                                  </TableCell>
                                  <TableCell>
                                    {formatDateTime(
                                      phaseDetails.transcodeCancelledAt
                                    )}
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          )}

                          <TableRow>
                            <TableCell>
                              <strong>Last Status Change</strong>
                            </TableCell>
                            <TableCell>
                              {formatDateTime(phaseDetails?.lastEncoreChangeAt)}
                            </TableCell>
                          </TableRow>
                          {!!phaseDetails?.encorePollFailures && (
                            <TableRow>
                              <TableCell>
                                <strong>Failed Status Checks</strong>
                              </TableCell>
                              <TableCell>
                                {phaseDetails.encorePollFailures} in a row — we
                                have not been able to reach the transcoder
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell>
                              <strong>Ready For Packaging</strong>
                            </TableCell>
                            <TableCell>
                              {formatDateTime(phaseDetails?.encoreFinishedAt)}
                            </TableCell>
                          </TableRow>

                          {transcoderJobResponse && (
                            <TableRow>
                              <TableCell colSpan={2} sx={{ maxWidth: 0 }}>
                                <Accordion>
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                    >
                                      {TRANSCODER_LABEL[transcoder]} Job
                                      Response (technical detail)
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <Box
                                      component="pre"
                                      sx={{
                                        backgroundColor: "grey.900",
                                        color: "grey.100",
                                        p: 2,
                                        borderRadius: 1,
                                        overflow: "auto",
                                        overflowWrap: "break-word",
                                        wordBreak: "break-all",
                                        whiteSpace: "pre-wrap",
                                        fontSize: "0.75rem",
                                        maxHeight: "400px",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {JSON.stringify(
                                        transcoderJobResponse,
                                        null,
                                        2
                                      )}
                                    </Box>
                                  </AccordionDetails>
                                </Accordion>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )}

                      {/* Ingesting/publishing/completed phases don't show Encore details - only Alcom */}

                      {viewedPhaseName === "ingesting" && (
                        <>
                          <TableRow>
                            <TableCell>
                              <strong>Alcom State</strong>
                            </TableCell>
                            <TableCell>
                              {viewedPhaseData.details?.alcomState || "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>Playable</strong>
                            </TableCell>
                            <TableCell>
                              {viewedPhaseData.details?.alcomPlayable !==
                              undefined
                                ? viewedPhaseData.details.alcomPlayable
                                  ? "Yes"
                                  : "No"
                                : "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>Content Duration</strong>
                            </TableCell>
                            <TableCell>
                              {viewedPhaseData.details?.alcomContentDurationMs
                                ? `${Math.round(
                                    parseInt(
                                      viewedPhaseData.details
                                        .alcomContentDurationMs
                                    ) / 1000
                                  )}s`
                                : "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>Alcom Created</strong>
                            </TableCell>
                            <TableCell>
                              {formatDateTime(
                                viewedPhaseData.details?.alcomCreationTime
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>Alcom Modified</strong>
                            </TableCell>
                            <TableCell>
                              {formatDateTime(
                                viewedPhaseData.details?.alcomModificationTime
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>DASH URI</strong>
                            </TableCell>
                            <TableCell sx={{ wordBreak: "break-all" }}>
                              {viewedPhaseData.details?.alcomDeliveryUris
                                ?.dash || "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>HLS URI</strong>
                            </TableCell>
                            <TableCell sx={{ wordBreak: "break-all" }}>
                              {viewedPhaseData.details?.alcomDeliveryUris
                                ?.hls || "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>Additional Info</strong>
                            </TableCell>
                            <TableCell>
                              {viewedPhaseData.details?.alcomAdditionalInfo ||
                                "N/A"}
                            </TableCell>
                          </TableRow>
                        </>
                      )}

                      {/* Publishing and Completed show only manifest URLs */}
                      {(viewedPhaseName === "publishing" ||
                        viewedPhaseName === "completed") && (
                        <>
                          <TableRow>
                            <TableCell>
                              <strong>DASH Manifest</strong>
                            </TableCell>
                            <TableCell sx={{ wordBreak: "break-all" }}>
                              {job?.manifestUrls?.dash || "N/A"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <strong>HLS Manifest</strong>
                            </TableCell>
                            <TableCell sx={{ wordBreak: "break-all" }}>
                              {job?.manifestUrls?.hls || "N/A"}
                            </TableCell>
                          </TableRow>
                        </>
                      )}

                      {/* Error row at the end - shows for any phase with an error */}
                      {viewedPhaseData.error && (
                        <>
                          <TableRow>
                            <TableCell>
                              <strong>Error</strong>
                            </TableCell>
                            <TableCell>
                              <Typography color="error">
                                {viewedPhaseData.error}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={2} sx={{ pt: 2 }}>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<ReplayIcon />}
                                onClick={handleRetry}
                                fullWidth
                              >
                                Retry {viewedPhaseName} Phase
                              </Button>
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Transcoding progress bar */}
          {job &&
            phase === "transcoding" &&
            typeof job.currentPhase.progress === "number" && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.disabled" gutterBottom>
                  Transcode progress: {job.currentPhase.progress}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={job.currentPhase.progress}
                />
              </Box>
            )}

          {/* Completed alert */}
          {phase === "completed" && (
            <Alert
              severity="success"
              icon={<CheckCircleOutlineIcon />}
              sx={{ mb: 2 }}
            >
              Ingest complete. Asset is playable.
            </Alert>
          )}

          {/* Incidents (metadata warnings, etc.) */}
          {job && job.incidents && job.incidents.length > 0 && (
            <Card
              sx={{
                mb: 2,
                bgcolor: "warning.lighter",
                border: "1px solid",
                borderColor: "warning.main",
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: "warning.dark" }}>
                  ⚠ Issues During Processing ({job.incidents.length})
                </Typography>
                <IncidentList incidents={job.incidents} />
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Error banner */}
      {error && (
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon />}
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Upload controls — only shown when no job exists */}
      {!job && (
        <>
          {/* Subtitle upload */}
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="h6">Upload subtitles</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              One file per language. Language is stored as blob metadata and
              used in SMIL. One request per file.
            </Typography>
            <Stack spacing={1} sx={{ mb: 1 }}>
              {subtitleRows.map((row) => (
                <Stack
                  key={row.id}
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  flexWrap="wrap"
                >
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Language</InputLabel>
                    <Select
                      value={row.language}
                      label="Language"
                      onChange={(e) =>
                        updateSubtitleRow(row.id, { language: e.target.value })
                      }
                    >
                      {SUBTITLE_LANGUAGES.map((l) => (
                        <MenuItem key={l.code} value={l.code}>
                          {l.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button size="small" variant="outlined" component="label">
                    Select file
                    <input
                      hidden
                      type="file"
                      accept=".vtt,.srt,text/vtt,application/x-subrip"
                      onChange={(e) => {
                        updateSubtitleRow(row.id, {
                          file: e.target.files?.[0] ?? null,
                        });
                        e.target.value = "";
                      }}
                    />
                  </Button>
                  <Typography variant="body2" sx={{ alignSelf: "center" }}>
                    {row.file ? row.file.name : "No file"}
                  </Typography>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => removeSubtitleRow(row.id)}
                    disabled={subtitleRows.length <= 1}
                  >
                    Remove
                  </Button>
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button size="small" variant="outlined" onClick={addSubtitleRow}>
                Add language
              </Button>
              <Button
                variant="contained"
                disabled={
                  subtitleUploading || !subtitleRows.some((r) => r.file)
                }
                onClick={handleSubtitleUpload}
              >
                {subtitleUploading
                  ? "Uploading…"
                  : `Upload ${subtitleRows.filter((r) => r.file).length} file(s)`}
              </Button>
            </Stack>
            {subtitleMessage && (
              <Alert severity="success" sx={{ mt: 1 }}>
                {subtitleMessage}
              </Alert>
            )}
            {subtitleError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {subtitleError}
              </Alert>
            )}
          </Box>

          {/* Video upload */}
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="h6">Entitlements</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Select media collections to add this asset to after ingest
              completes.
            </Typography>
            <Autocomplete
              multiple
              options={collections}
              disableCloseOnSelect
              getOptionLabel={(option) => option.name || option._id}
              value={collections.filter((c) =>
                selectedEntitlements.includes(c._id)
              )}
              onChange={(_event, newValue) => {
                setSelectedEntitlements(newValue.map((c) => c._id));
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.name || option._id}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Media Collections"
                  placeholder="Search collections…"
                />
              )}
              sx={{ maxWidth: 500 }}
            />
          </Box>

          {/* Video upload */}
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="h6">Upload VOD MP4</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Uploads as filename: <strong>{assetId}.mp4</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              To include separate subtitle files for ingest, make sure you
              upload these before uploading the video.
            </Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Button variant="outlined" component="label">
                Select MP4
                <input
                  ref={fileInputRef}
                  hidden
                  type="file"
                  accept="video/mp4"
                  onChange={handleFileChange}
                />
              </Button>
              <Typography variant="body2">
                {selectedFile ? selectedFile.name : "No file selected"}
              </Typography>
              <Button
                variant="contained"
                disabled={!selectedFile || busy}
                onClick={handleUpload}
              >
                {busy ? "Uploading..." : "Upload to ingest"}
              </Button>
            </Stack>
          </Box>
        </>
      )}

      {/* Upload progress bar */}
      {phase === "uploading" && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.disabled" gutterBottom>
            Uploading… {uploadProgress}%
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}
    </Box>
  );
};

export default IngestPanel;
